/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import Breadcrumb from '@/components/AZCustomComponent/BreadcrumbSTARC/Breadcrumb';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import { eventTypes } from '@/constants/event';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { useSeoMetadata } from '@/features/common/api/getSeoMetadata';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { getProductDetailsURL } from '@/features/product';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { siteSections } from '@/utils/siteSections';
import { useDeviceType } from '@/utils/useDeviceType';
import { Skeleton, View } from '@az/starc-ui';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useProductReviews } from '../../api/getProductReviews';
import { REVIEWS_DEFAULT_SORTING } from '../../constants';
import { useReviewImageModal } from '../../hooks/useReviewImageModal';
import { useReviewsRedirect } from '../../hooks/useReviewsRedirect';
import type { ProductReviewsRouterQuery, ReviewsSorting } from '../../interface';
import { CustomerImages } from '../CustomerImages/CustomerImages';
import { ProductInformation } from '../ProductInformation/ProductInformation';
import { ProductReviewsMetadata } from '../ProductReviewsMetadata/ProductReviewsMetadata';
import { RatingDistributionFilter } from '../RatingDistributionFilter/RatingDistributionFilter';
import { ReviewsList } from '../ReviewsList/ReviewsList';
import styles from './styles.module.scss';
import { ReviewCTAButtons } from '../ReviewCTAButtons/ReviewCTAButtons';
import { CustomerImagesModal } from '../CustomerImagesModal/CustomerImagesModal';
import { getWindowLocation } from '@/utils/getWindowLocation';
import { keepPreviousData } from '@tanstack/react-query';

const LazyNotification = dynamic(() =>
  import('@az/starc-ui/dist/components/notification').then((mod) => mod.Notification)
);

const labelMap = {
  ratingsAndReviews: 'label_reviews_ratingsAndReviews',
  goToProduct: 'label_reviews_goToProduct',
  writeAReview: 'label_reviews_writeAReview',
  of: 'label_of',
  reviews: 'label_reviews',
  customerReviews: 'label_reviews_customerReviews',
  errorOccurred: 'label_reviews_errorOcurred',
};

export function ProductReviews() {
  const [analyticsExecuted, setAnalyticsExecuted] = useState(false);
  const labels = useLabels(labelMap);
  const router = useRouter();
  const { slug } = router.query as ProductReviewsRouterQuery;
  const locale = useLocale();
  const hasSetupAnalytics = useAnalytics();
  const pageViewCall = usePageViewCall();

  useReviewsRedirect();

  const {
    isOpen,
    images,
    activeImage,
    activeReview,
    hasBackButton,
    onImageChange,
    openModal,
    closeModal,
  } = useReviewImageModal();

  const { data: productDetails, error: productDetailsError } = useProductDetails();

  const { data: seoMetaData } = useSeoMetadata({ enabled: true });

  const { pdpURL, breadcrumbs } = useMemo(() => {
    const reviewsURL = `/${slug.join('/')}`;
    const pdpURL = getProductDetailsURL(reviewsURL);

    const breadcrumbs = productDetails?.product?.itemDescription
      ? [
          { label: productDetails.product.itemDescription, url: pdpURL },
          { label: labels.customerReviews, url: reviewsURL },
        ]
      : undefined;

    return {
      pdpURL,
      breadcrumbs,
    };
  }, [slug, labels.customerReviews, productDetails?.product?.itemDescription]);

  const isBot = useDeviceType() === 'bot';

  const [pageNumber, setPageNumber] = useState<number>(isBot ? 0 : 1);
  const [sortBy, setSortBy] = useState<ReviewsSorting | undefined>(REVIEWS_DEFAULT_SORTING);
  const [starFilter, setStarFilter] = useState<number[]>([]);

  useEffect(() => {
    if (isBot) {
      return;
    }
    setPageNumber(1);
  }, [starFilter, sortBy, isBot]);

  const {
    data: productReviews,
    isFetching,
    error: reviewsError,
    isSuccess: reviewsSuccess,
  } = useProductReviews({
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    skuId: productDetails?.product?.itemId ?? '',
    pageNumber,
    sortBy,
    filterByStar: starFilter,
    enabled: !!productDetails?.product?.itemId,
    placeholderData: keepPreviousData,
  });

  const [showError, setShowError] = useState(false);

  const { currentPageReviewStart, currentPageReviewEnd, resultReviewCount } = productReviews ?? {};

  const pageInformation = useMemo(() => {
    return currentPageReviewStart !== undefined &&
      currentPageReviewEnd !== undefined &&
      resultReviewCount !== undefined
      ? `${currentPageReviewStart.toLocaleString(locale)} - ${currentPageReviewEnd.toLocaleString(
          locale
        )} ${labels.of} ${resultReviewCount.toLocaleString(locale)} ${labels.reviews}`
      : null;
  }, [
    currentPageReviewEnd,
    currentPageReviewStart,
    labels.of,
    labels.reviews,
    resultReviewCount,
    locale,
  ]);

  useEffect(() => {
    if (productDetailsError || reviewsError) {
      setShowError(true);
    }
  }, [productDetailsError, reviewsError]);

  useEffect(() => {
    if (!analyticsExecuted && hasSetupAnalytics && reviewsSuccess) {
      setAnalyticsExecuted(true);
      const location = getWindowLocation();
      const pageSpecificAnalyticsData = {
        pageName: getPageName(pageTypes.reviews, location.pathname),
        pageType: pageTypes.reviews,
        seoPageType: getSeoPageType(pageTypes.reviews, location.pathname),
        siteSection: siteSections.reviews,
        prodView: eventTypes.prodView,
      };
      pageViewCall(
        pageSpecificAnalyticsData,
        getAnalyticsRelativeComplementOf(pageSpecificAnalyticsData, undefined)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsExecuted, hasSetupAnalytics, reviewsSuccess]);

  return (
    <View padding={4}>
      <ProductReviewsMetadata seoMetaData={seoMetaData} reviewsData={productReviews} />

      {showError && (
        <View className={styles.errorNotification}>
          <LazyNotification
            type="error"
            text={labels.errorOccurred}
            onClose={() => setShowError(false)}
            hasCloseButton
          />
        </View>
      )}

      <View className={styles.breadcrumbsWrapper}>
        {breadcrumbs ? (
          <Breadcrumb data={breadcrumbs} customClass={styles.breadcrumbs} lastItemDisabled />
        ) : (
          <Skeleton width="100%" className={styles.breadcrumbsSkeleton} />
        )}
      </View>

      {images ? (
        <CustomerImagesModal
          isOpen={isOpen}
          hasBackButton={hasBackButton}
          onClose={closeModal}
          image={activeImage}
          images={images}
          review={activeReview}
          onImageChange={onImageChange}
        />
      ) : null}

      <View>
        <View className={styles.topContainer}>
          <View className={styles.imageContainer}>
            <NextImage
              src={
                productDetails?.product?.itemImageUrl
                  ? productDetails.product.itemImageUrl
                  : blurDataURLPlaceholder
              }
              width={300}
              height={300}
              className={styles.productImage}
              priority
              alt={productDetails?.product?.itemDescription}
            />
          </View>

          <View
            className={styles.productInformationContainer}
            attributes={{ 'data-testid': 'productInformation' }}
          >
            <ProductInformation
              averageRating={productReviews?.averageRating}
              totalReviewCount={productReviews?.totalReviewCount}
            />
          </View>

          <View
            className={styles.ratingDistributionContainer}
            attributes={{ 'data-testid': 'distribution' }}
          >
            <RatingDistributionFilter
              ratingDistribution={productReviews?.ratingsDistribution ?? null}
              starFilter={starFilter}
              setStarFilter={setStarFilter}
              totalReviews={productReviews?.totalReviewCount ?? null}
            />
          </View>

          <ReviewCTAButtons className={styles.buttonsContainer} pdpURL={pdpURL} />
        </View>

        <View
          className={cx(styles.fullWidthContainer, styles.imagesSectionContainer)}
          attributes={{ 'data-testid': 'customerImages' }}
        >
          <CustomerImages onImageClick={onImageChange} onSeeAllClick={openModal} />
        </View>

        <View className={styles.fullWidthContainer}>
          <ReviewsList
            reviews={productReviews?.reviews}
            pageInformation={pageInformation}
            totalPages={productReviews?.pageNumbers}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            sortBy={sortBy}
            setSortBy={setSortBy}
            starFilter={starFilter}
            setStarFilter={setStarFilter}
            loading={isFetching}
            onReviewImageClick={onImageChange}
          />
        </View>
      </View>
    </View>
  );
}
